import { z } from "zod";

/**
 * Field rules shared with the API.
 *
 * The server enforces these regardless; mirroring them here means a mistyped
 * Emirates ID is caught while the employee is still looking at the field
 * rather than after they press Continue.
 */

const DIGITS = /\D/g;

/** UAE mobile: 050 123 4567 in any of the shapes people type it. */
export function normaliseUaeMobile(value: string): string | null {
  let digits = value.replace(DIGITS, "");
  if (!digits) return null;
  if (digits.startsWith("00971")) digits = digits.slice(5);
  else if (digits.startsWith("971")) digits = digits.slice(3);
  else if (digits.startsWith("0")) digits = digits.slice(1);
  return digits.length === 9 && digits.startsWith("5") ? `+971${digits}` : null;
}

/** Any plausible international number: an emergency contact may be abroad. */
export function normalisePhone(value: string): string | null {
  const uae = normaliseUaeMobile(value);
  if (uae) return uae;
  let digits = value.replace(DIGITS, "");
  if (value.trim().startsWith("00")) digits = digits.slice(2);
  return digits.length >= 7 && digits.length <= 15 ? `+${digits}` : null;
}

export function normaliseEmiratesId(value: string): string | null {
  const raw = value.replace(DIGITS, "");
  if (raw.length !== 15 || !raw.startsWith("784")) return null;
  return `${raw.slice(0, 3)}-${raw.slice(3, 7)}-${raw.slice(7, 14)}-${raw.slice(14)}`;
}

const IBAN_LENGTHS: Record<string, number> = {
  AE: 23, GB: 22, SA: 24, IN: 34, PK: 24, EG: 29, JO: 30,
};

/** ISO 13616 mod-97, so a single mistyped digit is caught before submission. */
export function ibanIsValid(value: string): boolean {
  const compact = value.replace(/[\s-]/g, "").toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(compact)) return false;
  const expected = IBAN_LENGTHS[compact.slice(0, 2)];
  if (expected !== undefined && compact.length !== expected) return false;
  if (compact.length < 15 || compact.length > 34) return false;

  const rearranged = compact.slice(4) + compact.slice(0, 4);
  const numeric = rearranged.replace(/[A-Z]/g, (c) => String(c.charCodeAt(0) - 55));
  // The number is longer than a JS integer, so take the modulus in chunks.
  let remainder = 0;
  for (const digit of numeric) {
    remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder === 1;
}

export function normaliseIban(value: string): string | null {
  const compact = value.replace(/[\s-]/g, "").toUpperCase();
  return ibanIsValid(compact) ? compact : null;
}

export function formatIban(value: string): string {
  return value.replace(/(.{4})/g, "$1 ").trim();
}

export function calculateAge(isoDate: string): number | null {
  const dob = new Date(isoDate);
  if (Number.isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const beforeBirthday =
    today.getMonth() < dob.getMonth() ||
    (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate());
  if (beforeBirthday) age -= 1;
  return age;
}

// ── Zod field builders ───────────────────────────────────────────────────────

export const uaeMobile = (message = "Enter a UAE mobile number, for example 050 123 4567.") =>
  z.string().refine((v) => normaliseUaeMobile(v) !== null, { message });

export const anyPhone = (message = "Enter a valid phone number with its country code.") =>
  z.string().refine((v) => normalisePhone(v) !== null, { message });

export const emiratesId = (message = "An Emirates ID is 15 digits beginning 784.") =>
  z.string().refine((v) => normaliseEmiratesId(v) !== null, { message });

export const iban = (message = "That IBAN is not valid. Check it against your bank statement.") =>
  z.string().refine((v) => ibanIsValid(v), { message });

export const requiredText = (label: string, max = 200) =>
  z.string().trim().min(1, `${label} is required.`).max(max);

export const isoDate = (label: string) =>
  z.string().min(1, `${label} is required.`).refine(
    (v) => !Number.isNaN(new Date(v).getTime()),
    { message: `Enter a valid ${label.toLowerCase()}.` },
  );

/** A date that must be in the future, used for document expiry. */
export const futureDate = (label: string) =>
  isoDate(label).refine((v) => new Date(v) > new Date(), {
    message: `${label} is in the past. Check the date on the document.`,
  });

/**
 * How far past its issue date a passport may run. The UAE and many other
 * countries issue them for ten years, and some (the UK, until 2018) added the
 * months left on the old one, so a year's slack covers those while still
 * catching 2063 typed for 2036. The API holds the same line.
 */
export const PASSPORT_MAX_YEARS = 11;

/** The same day so many years on; 29 February lands on the 28th. */
function yearsAfter(day: string, years: number): string {
  const [year = 0, month = 1, date = 1] = day.slice(0, 10).split("-").map(Number);
  const leap = (y: number) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
  const target = year + years;
  const clamped = month === 2 && date === 29 && !leap(target) ? 28 : date;
  return `${target}-${String(month).padStart(2, "0")}-${String(clamped).padStart(2, "0")}`;
}

/**
 * Why a passport expiry date cannot be right, or null when it can. Measured
 * from the issue date when there is one, otherwise from today.
 */
export function passportExpiryProblem(
  issue: string | null | undefined,
  expiry: string | null | undefined,
  today: Date = new Date(),
): string | null {
  if (!expiry || !/^\d{4}-\d{2}-\d{2}/.test(expiry)) return null;
  const from =
    issue && /^\d{4}-\d{2}-\d{2}/.test(issue)
      ? issue
      : `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  // ISO dates compare correctly as strings, with no time zone to trip over.
  if (expiry.slice(0, 10) > yearsAfter(from, PASSPORT_MAX_YEARS)) {
    return "A passport is valid for ten years at most. Check the expiry date on it.";
  }
  return null;
}
